import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage, useIntl } from 'react-intl'
import { useForm } from 'react-hook-form'
import {
    Box,
    Text,
    Button,
    Image,
    Checkbox,
    Alert,
    AlertIcon
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import { CheckCircleIcon } from '../../../components/icons'
import { generateStyles } from '../../../components/optimizely/utils/utils'
import { useCustomerId, useShopperCustomersMutation } from '@salesforce/commerce-sdk-react'
import usedDeviceDetection from '../../../hooks/use-device-detection'
import { sendViewErrorEvent } from '../../../utils/gtm-utils'

/**
 * Newsletter subscribe form for the rewards page "Subscribe to our newsletter" popup.
 * Subscribe calls updateCustomer with c_email_garage_optedIn only (not c_email_garage_personalized_optedIn).
 * When customer is already subscribed (email_garage_optedIn true), shows completed state.
 */
const NewsletterSubscribeDrawer = ({
    popupData,
    styles,
    customer,
    onSuccess
}) => {
    const { formatMessage } = useIntl()
    const [isSubmitted, setIsSubmitted] = useState(false)
    const justSubscribedRef = useRef(false)
    const customerId = useCustomerId()
    const device = usedDeviceDetection()
    const { mutate: updateCustomer, isPending: isLoading } = useShopperCustomersMutation('updateCustomer')

    const form = useForm({
        defaultValues: {
            consent: false
        }
    })

    const { register, handleSubmit, formState: { errors }, clearErrors, setError } = form

    const isAlreadySubscribed = !!customer?.c_email_garage_optedIn

    const onSubmit = () => {
        if (!customerId) return
        updateCustomer(
            {
                parameters: { customerId },
                body: {
                    c_email_garage_optedIn: true,
                    c_device: device
                }
            },
            {
                onSuccess: () => {
                    justSubscribedRef.current = true
                    setIsSubmitted(true)
                    onSuccess?.()
                },
                onError: () => {
                    sendViewErrorEvent('Failed to subscribe to newsletter. Please try again.')
                    setError('global', {
                        type: 'manual',
                        message: formatMessage({
                            id: 'rewards.newsletter_subscribe.error_message',
                            defaultMessage: 'Failed to subscribe to newsletter. Please try again.'
                        })
                    })
                }
            }
        )
    }

    if (isSubmitted || isAlreadySubscribed) {
        return (
            <Box className="er-modal-tile">
                <CheckCircleIcon
                    width="38px"
                    height="38px"
                    marginTop="32px"
                    display="block"
                />
                <Text
                    fontSize={{ base: '20px' }}
                    fontWeight={700}
                    lineHeight={{ base: '20px' }}
                    textTransform="uppercase"
                    mt={{ base: '24px' }}
                >
                    {(isSubmitted || justSubscribedRef.current) ? (
                        <FormattedMessage
                            id="rewards.newsletter_subscribe.success_title"
                            defaultMessage="Thanks! You are officially subscribed to our newsletter & your points are on the way!"
                        />
                    ) : (
                        <FormattedMessage
                            id="rewards.newsletter_subscribe.already_subscribed_title"
                            defaultMessage="You're already subscribed to our newsletter."
                        />
                    )}
                </Text>
                {(isSubmitted || isAlreadySubscribed) && (
                    <Link to="/account/email" mt={4} display="inline-block" fontSize="sm" fontWeight={600} textDecoration="underline">
                        <FormattedMessage
                            id="rewards.newsletter_subscribe.manage_preferences"
                            defaultMessage="Manage preferences"
                        />
                    </Link>
                )}
            </Box>
        )
    }

    return (
        <Box className="er-modal-tile" as="form" onSubmit={handleSubmit(onSubmit)}>
            <Image {...styles?.erModalIcon} src={popupData?.icon} alt="" />
            <Text {...generateStyles(popupData?.titleStyle, { ...styles?.erModalTitle })}>
                {popupData?.title}
            </Text>
            <Box
                className="er-modal-subtitle"
                {...generateStyles(popupData?.descriptionStyle, { ...styles?.erModalSubtitle })}
                dangerouslySetInnerHTML={{ __html: popupData?.content }}
            />

            <Box mt={4} alignItems="flex-start" display="flex">
                <Checkbox
                    {...register('consent', {
                        required: formatMessage({
                            id: 'rewards.newsletter_subscribe.consent_required',
                            defaultMessage: 'You must agree to receive commercial messages to subscribe.'
                        })
                    })}
                    colorScheme="gray"
                    size="md"
                    alignItems="flex-start"
                    sx={{ '& .chakra-checkbox__control': { mt: '5px' } }}
                >
                    <Text as="span" fontSize="sm">
                        <FormattedMessage
                            id="rewards.newsletter_subscribe.consent_label"
                            defaultMessage="Yes, I would like to receive commercial electronic messages, including a weekly newsletter and discount notification. I understand I can unsubscribe at any time."
                        />
                    </Text>
                </Checkbox>
                {errors.consent && (
                    <Text color="red.500" fontSize="xs" mt={1}>
                        {errors.consent.message}
                    </Text>
                )}
            </Box>

            {form.formState.errors?.global && (
                <Alert status="error" mt={3}>
                    <AlertIcon color="red.500" boxSize={4} />
                    <Text fontSize="sm" ml={3}>
                        {form.formState.errors.global.message}
                    </Text>
                </Alert>
            )}

            <Button
                type="submit"
                mt={4}
                width="100%"
                isLoading={isLoading}
                isDisabled={isLoading}
                loadingText={formatMessage({ id: 'rewards.newsletter_subscribe.subscribing', defaultMessage: 'Subscribing...' })}
                size="lg"
                fontWeight="bold"
                onClick={() => clearErrors('global')}
            >
                <FormattedMessage
                    id="rewards.newsletter_subscribe.cta"
                    defaultMessage="Subscribe"
                />
            </Button>

            {popupData?.note && (
                <Box
                    className="er-modal-note"
                    mt={4}
                    {...generateStyles(popupData?.additionalInfoStyle, { ...styles?.erModalNote })}
                    dangerouslySetInnerHTML={{ __html: popupData.note }}
                />
            )}
        </Box>
    )
}

NewsletterSubscribeDrawer.propTypes = {
    popupData: PropTypes.shape({
        icon: PropTypes.string,
        title: PropTypes.string,
        content: PropTypes.string,
        note: PropTypes.string,
        titleStyle: PropTypes.object,
        descriptionStyle: PropTypes.object,
        additionalInfoStyle: PropTypes.object
    }).isRequired,
    styles: PropTypes.object,
    customer: PropTypes.object,
    onSuccess: PropTypes.func
}

export default NewsletterSubscribeDrawer
